import { GlassPanel } from "@/components/ui/glass-panel";
import { Car, AlertOctagon, AlertTriangle, ShieldCheck, Eye } from "lucide-react";
import { cn } from "@/lib/utils";
import { drivingRisk, type DrivingInput } from "@/lib/insights/driving-safety";

interface DrivingSafetyCardProps {
  current: {
    temp: number;
    dew_point?: number;
    visibility?: number;
    wind_speed?: number;
    wind_gust?: number;
    rain?: { "1h"?: number };
    snow?: { "1h"?: number };
    weather?: { id: number; main: string }[];
  };
}

const COLOR_MAP: Record<
  "emerald" | "amber" | "orange" | "red",
  { ring: string; text: string; bg: string; bar: string; chip: string }
> = {
  emerald: {
    ring: "ring-emerald-500/30",
    text: "text-emerald-400",
    bg: "bg-emerald-500/10",
    bar: "bg-emerald-400",
    chip: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30",
  },
  amber: {
    ring: "ring-amber-500/30",
    text: "text-amber-400",
    bg: "bg-amber-500/10",
    bar: "bg-amber-400",
    chip: "bg-amber-500/15 text-amber-300 border-amber-500/30",
  },
  orange: {
    ring: "ring-orange-500/30",
    text: "text-orange-400",
    bg: "bg-orange-500/10",
    bar: "bg-orange-400",
    chip: "bg-orange-500/15 text-orange-300 border-orange-500/30",
  },
  red: {
    ring: "ring-rose-500/30",
    text: "text-rose-400",
    bg: "bg-rose-500/10",
    bar: "bg-rose-400",
    chip: "bg-rose-500/15 text-rose-300 border-rose-500/30",
  },
};

export function DrivingSafetyCard({ current }: DrivingSafetyCardProps) {
  const input: DrivingInput = {
    tempC: current.temp,
    dewPointC: current.dew_point,
    visibilityMeters: current.visibility,
    windMs: current.wind_speed ?? 0,
    windGustMs: current.wind_gust,
    precipMm: current.rain?.["1h"],
    snowMm: current.snow?.["1h"],
    conditionMain: current.weather?.[0]?.main,
    conditionId: current.weather?.[0]?.id,
  };
  const risk = drivingRisk(input);
  const c = COLOR_MAP[risk.color];

  const Icon =
    risk.level === "Severe"
      ? AlertOctagon
      : risk.level === "Hazard"
        ? AlertTriangle
        : risk.level === "Caution"
          ? Eye
          : ShieldCheck;

  return (
    <GlassPanel
      intensity="low"
      className={cn("p-5 relative overflow-hidden", `!border-${risk.color}-500/20`)}
    >
      <div className={cn("absolute left-0 top-0 bottom-0 w-1", c.bar)} />

      <div className="flex items-start justify-between gap-4 mb-4">
        <div className="flex items-center gap-3">
          <div
            className={cn(
              "flex h-10 w-10 items-center justify-center rounded-xl ring-1",
              c.bg,
              c.ring,
            )}
          >
            <Car className={cn("h-5 w-5", c.text)} />
          </div>
          <div>
            <p className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground font-semibold">
              Driving Safety
            </p>
            <h3 className="text-base font-bold flex items-center gap-2">
              <Icon className={cn("h-4 w-4", c.text)} />
              {risk.level}
            </h3>
            <p className="text-xs text-muted-foreground mt-0.5">{risk.primary}</p>
          </div>
        </div>
        <div className={cn("rounded-xl px-3 py-2 ring-1 text-right flex-shrink-0", c.bg, c.ring)}>
          <p className="text-[9px] uppercase tracking-wider text-muted-foreground font-semibold">
            Risk
          </p>
          <p className={cn("text-xl font-bold tabular-nums", c.text)}>{risk.score}</p>
        </div>
      </div>

      <div className="h-1.5 rounded-full bg-border/40 overflow-hidden mb-4">
        <div
          className={cn("h-full rounded-full transition-all", c.bar)}
          style={{ width: `${risk.score}%` }}
        />
      </div>

      {risk.factors.length === 0 ? (
        <div className="rounded-lg bg-emerald-500/5 border border-emerald-500/20 px-3 py-2">
          <p className="text-xs text-emerald-300 font-medium">
            No road hazards detected. Drive normally.
          </p>
        </div>
      ) : (
        <div className="space-y-1.5">
          {risk.factors.map((f, i) => {
            const sevColor =
              f.severity === "high"
                ? "bg-rose-500/15 text-rose-300 border-rose-500/30"
                : f.severity === "med"
                  ? "bg-orange-500/15 text-orange-300 border-orange-500/30"
                  : "bg-amber-500/15 text-amber-300 border-amber-500/30";
            return (
              <div
                key={i}
                className="flex items-center gap-3 rounded-lg bg-background/40 border border-border/40 px-3 py-2"
              >
                <span
                  className={cn(
                    "inline-flex items-center justify-center px-1.5 py-0.5 rounded-md border text-[9px] uppercase tracking-wider font-bold flex-shrink-0",
                    sevColor,
                  )}
                >
                  {f.severity}
                </span>
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-semibold leading-tight">{f.kind}</p>
                  <p className="text-[10px] text-muted-foreground leading-tight">{f.note}</p>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <p className="mt-3 text-[10px] text-muted-foreground">
        Heuristic from visibility, temp, dew point, precipitation and wind. Not a
        substitute for road-condition reports.
      </p>
    </GlassPanel>
  );
}
